/**
 * TeacherOnlyGate.jsx — Verrou réutilisable "réservé aux enseignants".
 * Enveloppe un contenu et affiche un placeholder élégant si l'utilisateur
 * n'a pas le rôle `enseignant` ou `admin`.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GraduationCap, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { useApp } from '../contexts/AppContext';

export default function TeacherOnlyGate({
  children,
  title = 'Support pédagogique · réservé aux moniteurs',
  description = 'Ce contenu est un support de cours réservé aux enseignants Code 2 Roues connectés. Ton moniteur pourra te le projeter en séance ou te le partager si besoin.',
  accentColor = '#f43f5e',
  compact = false,
  testId = 'teacher-only-gate',
}) {
  const { user } = useApp();
  const location = useLocation();
  const isTeacher = Boolean(user) && ['enseignant', 'admin'].includes(user?.role);

  if (isTeacher) return children;

  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <section
      className={`${compact ? 'my-4' : 'my-8'} mx-auto max-w-3xl rounded-2xl border-2 print:hidden`}
      style={{
        borderColor: `${accentColor}55`,
        background: `linear-gradient(135deg, ${accentColor}18, ${accentColor}08, transparent)`,
      }}
      data-testid={testId}
      aria-live="polite"
    >
      <div className={`${compact ? 'p-4' : 'p-5 sm:p-6'} flex flex-col sm:flex-row sm:items-center gap-4`}>
        <div
          className="flex-shrink-0 mx-auto sm:mx-0 w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: `${accentColor}30`, border: `1px solid ${accentColor}60` }}
        >
          <GraduationCap className="w-7 h-7" style={{ color: accentColor }} />
        </div>
        <div className="flex-1 text-center sm:text-left">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[9.5px] font-black uppercase tracking-[0.22em] mb-1.5"
            style={{ background: `${accentColor}30`, border: `1px solid ${accentColor}60`, color: accentColor }}
          >
            <Lock className="w-3 h-3" /> Support enseignant · Réservé
          </div>
          <h3 className="text-lg sm:text-xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {title}
          </h3>
          <p className="mt-1 text-[13px] text-white/70 leading-snug">
            {description}
          </p>
        </div>
        <div className="flex flex-col gap-1.5 sm:flex-shrink-0">
          <Button
            asChild
            size="sm"
            style={{ background: accentColor }}
            className="hover:opacity-90 text-white font-black uppercase tracking-wider"
            data-testid={`${testId}-login`}
          >
            <Link to={`/login?next=${next}`}>Se connecter</Link>
          </Button>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="border-white/20 text-white/80 hover:bg-white/5"
            data-testid={`${testId}-signup`}
          >
            <Link to={`/register?next=${next}&role=enseignant`}>Compte moniteur</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
